import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FaFingerprint } from "react-icons/fa";
import { FiCheckCircle } from "react-icons/fi";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { InfoRow, Loading } from "../components/ui";
import { AppBar } from "../components/mobileUi";
import {
  DESK,
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../components/desktopUi";
import {
  getAttendanceCalendarByMonthApi,
  getAttendanceDetailByDayApi,
  getCheckinEligibilityApi,
} from "../api/attendance.api";
import { attendanceMeta } from "../utils/attendance";
import { fmtNumber, fmtTime, dayKey } from "../utils/format";
import { lunarLabel } from "../utils/lunar";
import { EMP_BASE_PATH } from "../config";

// ===== Tông màu khớp mobile-v2 (themes/colors/default.ts) =====
const PRIMARY = "#4081EB"; // primary200
const CARD_BORDER = "rgba(8, 20, 45, 0.06)";
const TEXT = "#08142D";
const MUTED = "#6B7280";

const WEEK_LABELS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
const MONTHS = [
  "Tháng 1", "Tháng 2", "Tháng 3", "Tháng 4", "Tháng 5", "Tháng 6",
  "Tháng 7", "Tháng 8", "Tháng 9", "Tháng 10", "Tháng 11", "Tháng 12",
];

// Legend theo đúng thứ tự app: 2 cột
const LEGEND_ITEMS = [
  { key: "CHECKIN_ON_TIME", label: "Đi làm" },
  { key: "LEAVE", label: "Nghỉ phép" },
  { key: "CHECKIN_LATE", label: "Đi muộn" },
  { key: "HALF_SHIFT", label: "Nửa ca" },
  { key: "ABSENT", label: "Vắng mặt" },
  { key: "MAKEUP_WORK", label: "Làm bù" },
  { key: "HOLIDAY", label: "Cuối tuần / Lễ" },
  { key: "REGISTER_OUTSIDE", label: "Làm ngoài" },
];

const DayDetail = ({ data }) => {
  if (!data) return <Text fontSize="13px">Không có dữ liệu.</Text>;
  const meta = attendanceMeta(data.attendanceType);
  const sub =
    data.checkin ||
    data.registerCheckin ||
    data.makeupWorkRequest ||
    data.leaveRequest ||
    {};
  return (
    <Box>
      <Flex align="center" gap="8px" mb="12px">
        <Box w="12px" h="12px" borderRadius="full" bg={meta.color} />
        <Text fontWeight={700} color={meta.color}>
          {meta.label}
        </Text>
      </Flex>
      <InfoRow label="Số công">{fmtNumber(data.workingDayRate)}</InfoRow>
      {data.checkinTime && <InfoRow label="Giờ chấm">{fmtTime(data.checkinTime)}</InfoRow>}
      {data.attendanceType === "LEAVE" && (
        <>
          <InfoRow label="Loại nghỉ">{sub.leaveTypeCode || "-"}</InfoRow>
          <InfoRow label="Số ngày">{sub.daysRequested ?? "-"}</InfoRow>
          <InfoRow label="Lý do">{sub.reason || "-"}</InfoRow>
        </>
      )}
      {data.attendanceType === "REGISTER_OUTSIDE" && (
        <>
          <InfoRow label="Địa điểm">{sub.checkinPlace || "-"}</InfoRow>
          <InfoRow label="Ghi chú">{sub.comment || "-"}</InfoRow>
        </>
      )}
      {data.attendanceType === "MAKEUP_WORK" && (
        <InfoRow label="Ghi chú">{sub.note || "-"}</InfoRow>
      )}
      {(data.checkin?.checkinPlaceObject || data.checkinPlaceObject) && (
        <InfoRow label="Nơi chấm">
          {data.checkin?.checkinPlaceObject?.identifier ||
            data.checkin?.checkinPlaceObject?.name ||
            data.checkinPlaceObject?.identifier ||
            data.checkinPlaceObject?.name ||
            "-"}
        </InfoRow>
      )}
    </Box>
  );
};

const AttendanceCalendar = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [cursor, setCursor] = useState(moment().startOf("month"));
  const [selectedDate, setSelectedDate] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const picker = useDisclosure();
  const [draftMonth, setDraftMonth] = useState(cursor.month());
  const [draftYear, setDraftYear] = useState(cursor.year());

  const monthParam = cursor.format("MM-YYYY");

  const calQuery = useQuery({
    queryKey: ["emp", "calendar", monthParam],
    queryFn: () => getAttendanceCalendarByMonthApi(monthParam),
  });

  const eligibilityQuery = useQuery({
    queryKey: ["emp", "eligibility", dayKey()],
    queryFn: () => getCheckinEligibilityApi(dayKey()),
  });

  const detailQuery = useQuery({
    queryKey: ["emp", "day-detail", selectedDate],
    queryFn: () => getAttendanceDetailByDayApi(selectedDate),
    enabled: !!selectedDate && isOpen,
  });

  const statusByDate = calQuery.data?.statusByDate || {};
  const legendCounts = calQuery.data?.legendCounts || {};
  const legendRate = calQuery.data?.legendWorkingDayRate || legendCounts;

  const eligibility = eligibilityQuery.data;
  const checkedIn = eligibility?.checkedIn;
  const canCheckIn = eligibility?.canCheckIn;
  const bannerText = checkedIn
    ? eligibility?.message || "Hôm nay bạn đã chấm công"
    : eligibility?.message ||
      (eligibility?.window?.start
        ? `Bạn có thể chấm công trong khung ${eligibility.window.start} - ${eligibility.window.end}`
        : "Bạn chưa chấm công");

  // Lưới ngày: bắt đầu từ T2 của tuần chứa ngày 1
  const days = useMemo(() => {
    const start = cursor.clone().startOf("month").startOf("isoWeek");
    const end = cursor.clone().endOf("month").endOf("isoWeek");
    const arr = [];
    const d = start.clone();
    while (d.isSameOrBefore(end, "day")) {
      arr.push(d.clone());
      d.add(1, "day");
    }
    return arr;
  }, [cursor]);

  const openDay = (dateStr) => {
    setSelectedDate(dateStr);
    onOpen();
  };

  const openPicker = () => {
    setDraftMonth(cursor.month());
    setDraftYear(cursor.year());
    picker.onOpen();
  };
  const applyPicker = () => {
    setCursor(moment().year(draftYear).month(draftMonth).startOf("month"));
    picker.onClose();
  };

  const goDetail = () =>
    navigate(`${EMP_BASE_PATH}/chuyen-can/chi-tiet?month=${cursor.format("YYYY-MM")}`);

  const modals = (
    <>
      {/* ===== Modal chọn tháng/năm ===== */}
      <Modal isOpen={picker.isOpen} onClose={picker.onClose} isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.35)" />
        <ModalContent mx="16px" borderRadius="16px">
          <ModalBody p="14px">
            <Text fontSize="15px" fontWeight={700} color={TEXT} mb="10px">
              Chọn tháng
            </Text>
            <Grid templateColumns="repeat(3, 1fr)" gap="10px">
              {MONTHS.map((label, idx) => {
                const active = idx === draftMonth;
                return (
                  <Flex
                    key={label}
                    as="button"
                    onClick={() => setDraftMonth(idx)}
                    minH="44px"
                    align="center"
                    justify="center"
                    borderRadius="12px"
                    border="1px solid"
                    borderColor={active ? PRIMARY : CARD_BORDER}
                    bg={active ? PRIMARY : "white"}
                    color={active ? "white" : TEXT}
                    fontSize="14px"
                  >
                    {label}
                  </Flex>
                );
              })}
            </Grid>

            <Text fontSize="15px" fontWeight={700} color={TEXT} mt="14px" mb="10px">
              Chọn năm
            </Text>
            <Flex align="center" border="1px solid" borderColor={CARD_BORDER} borderRadius="14px" h="48px" overflow="hidden">
              <Flex as="button" onClick={() => setDraftYear((y) => y - 10)} w="48px" h="48px" align="center" justify="center" fontWeight={700}>«</Flex>
              <Flex as="button" onClick={() => setDraftYear((y) => y - 1)} w="48px" h="48px" align="center" justify="center" fontWeight={700}>‹</Flex>
              <Flex flex="1" align="center" justify="center" fontWeight={700} color={TEXT}>{draftYear}</Flex>
              <Flex as="button" onClick={() => setDraftYear((y) => y + 1)} w="48px" h="48px" align="center" justify="center" fontWeight={700}>›</Flex>
              <Flex as="button" onClick={() => setDraftYear((y) => y + 10)} w="48px" h="48px" align="center" justify="center" fontWeight={700}>»</Flex>
            </Flex>

            <Flex gap="10px" mt="12px" mb="4px">
              <Flex as="button" onClick={picker.onClose} flex="1" h="46px" align="center" justify="center" borderRadius="12px" border="1px solid" borderColor={CARD_BORDER} fontWeight={700} color={TEXT}>
                Huỷ
              </Flex>
              <Flex as="button" onClick={applyPicker} flex="1" h="46px" align="center" justify="center" borderRadius="12px" bg={PRIMARY} color="white" fontWeight={700}>
                Xong
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* ===== Modal chi tiết ngày ===== */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent mx="16px">
          <ModalHeader fontSize="16px">
            Chi tiết ngày {selectedDate ? moment(selectedDate).format("DD/MM/YYYY") : ""}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="20px">
            {detailQuery.isLoading ? (
              <Loading label="Đang tải..." />
            ) : detailQuery.isError ? (
              <Text fontSize="13px" color={MUTED}>
                Không có dữ liệu chấm công cho ngày này.
              </Text>
            ) : (
              <DayDetail data={detailQuery.data} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  // Cùng query, cùng handler; chỉ trình bày lại theo style admin.
  if (isDesktop) {
    return (
      <DesktopPage
        title="Chuyên cần"
        right={
          <DesktopButton variant="outline" onClick={goDetail}>
            Xem chi tiết
          </DesktopButton>
        }
      >
        <DesktopPanel>
          <Flex align="center" justify="space-between" gap="16px" mb="16px" flexWrap="wrap">
            <Flex align="center" gap="8px">
              <Flex
                as="button"
                onClick={() => setCursor((c) => c.clone().subtract(1, "month"))}
                w="32px"
                h="32px"
                borderRadius="8px"
                border="1px solid"
                borderColor={DESK.border}
                align="center"
                justify="center"
                fontWeight={700}
                color={DESK.info}
                aria-label="Tháng trước"
              >
                ‹
              </Flex>
              <Text
                as="button"
                onClick={openPicker}
                fontSize="18px"
                fontWeight={600}
                color={DESK.title}
                px="8px"
              >
                Tháng {cursor.format("MM/YYYY")}
              </Text>
              <Flex
                as="button"
                onClick={() => setCursor((c) => c.clone().add(1, "month"))}
                w="32px"
                h="32px"
                borderRadius="8px"
                border="1px solid"
                borderColor={DESK.border}
                align="center"
                justify="center"
                fontWeight={700}
                color={DESK.info}
                aria-label="Tháng sau"
              >
                ›
              </Flex>
            </Flex>

            <Flex align="center" gap="16px">
              {checkedIn ? (
                <Flex align="center" gap="8px">
                  <Box as={FiCheckCircle} color="#18C758" fontSize="20px" />
                  <Text fontSize="14px" fontWeight={600} color={DESK.info}>
                    {bannerText}
                  </Text>
                </Flex>
              ) : (
                <>
                  <Text fontSize="14px" fontWeight={600} color={DESK.info}>
                    {bannerText}
                  </Text>
                  <DesktopButton
                    onClick={() => canCheckIn && navigate(`${EMP_BASE_PATH}/cham-cong`)}
                    isDisabled={!canCheckIn}
                    icon={<Box as={FaFingerprint} fontSize="18px" />}
                  >
                    Chấm công
                  </DesktopButton>
                </>
              )}
            </Flex>
          </Flex>

          {calQuery.isLoading ? (
            <Loading label="Đang tải lịch..." />
          ) : (
            <>
              <Grid templateColumns="repeat(7, 1fr)" mb="8px">
                {WEEK_LABELS.map((w) => (
                  <Text
                    key={w}
                    textAlign="center"
                    fontSize="13px"
                    fontWeight={700}
                    color={DESK.label}
                    py="8px"
                  >
                    {w}
                  </Text>
                ))}
              </Grid>
              <Grid templateColumns="repeat(7, 1fr)" gap="8px">
                {days.map((d) => {
                  const key = d.format("YYYY-MM-DD");
                  const inMonth = d.month() === cursor.month();
                  const type = statusByDate[key];
                  const meta = type ? attendanceMeta(type) : null;
                  const isToday = d.isSame(moment(), "day");
                  return (
                    <Flex
                      key={key}
                      as="button"
                      onClick={() => inMonth && openDay(key)}
                      direction="column"
                      align="center"
                      justify="center"
                      h="84px"
                      borderRadius="10px"
                      bg={meta ? meta.color : "white"}
                      border="1px solid"
                      borderColor={isToday ? PRIMARY : CARD_BORDER}
                      opacity={inMonth ? 1 : 0.35}
                      cursor={inMonth ? "pointer" : "default"}
                      _hover={inMonth ? { opacity: 0.9 } : {}}
                      transition="opacity .1s"
                    >
                      <Text
                        fontSize="18px"
                        fontWeight={700}
                        color={meta ? "white" : TEXT}
                        lineHeight="1.1"
                      >
                        {d.date()}
                      </Text>
                      <Text
                        fontSize="12px"
                        color={meta ? "rgba(255,255,255,0.85)" : "#9CA3AF"}
                        mt="4px"
                        lineHeight="1"
                      >
                        {lunarLabel(key)}
                      </Text>
                    </Flex>
                  );
                })}
              </Grid>
            </>
          )}
        </DesktopPanel>

        <DesktopPanel>
          <Text fontSize="16px" fontWeight={600} color={DESK.title} mb="12px">
            Chú thích
          </Text>
          <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap="12px">
            {LEGEND_ITEMS.map((it) => {
              const meta = attendanceMeta(it.key);
              return (
                <Flex key={it.key} align="center" gap="8px">
                  <Box
                    w="16px"
                    h="16px"
                    borderRadius="5px"
                    bg={meta.color}
                    border="1px solid"
                    borderColor={CARD_BORDER}
                    flexShrink={0}
                  />
                  <Text fontSize="13px" color={DESK.label}>
                    {it.label}: {legendRate?.[it.key] ?? 0}
                  </Text>
                </Flex>
              );
            })}
            <Flex align="center" gap="8px">
              <Box
                w="16px"
                h="16px"
                borderRadius="5px"
                bg="white"
                border="1px solid"
                borderColor={PRIMARY}
                flexShrink={0}
              />
              <Text fontSize="13px" color={DESK.label}>
                Chưa chấm công: {legendCounts?.MISSING ?? 0}
              </Text>
            </Flex>
          </Grid>
        </DesktopPanel>

        {modals}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="90px">
      <AppBar title="Chấm công" onBack={() => navigate(EMP_BASE_PATH)} />
      <Box px="0" pt="8px">
      {/* ===== Header xanh: điều hướng tháng + Chi tiết ===== */}
      <Flex align="center" bg={PRIMARY} h="48px" px="12px" borderTopRadius="4px">
        <Flex align="center" gap="12px" flex="1">
          <Flex
            as="button"
            onClick={() => setCursor((c) => c.clone().subtract(1, "month"))}
            w="24px"
            h="24px"
            borderRadius="4px"
            bg="rgba(255,255,255,0.15)"
            align="center"
            justify="center"
            color="white"
            fontSize="13px"
            fontWeight={700}
            aria-label="Tháng trước"
          >
            «
          </Flex>
          <Text
            as="button"
            onClick={openPicker}
            color="white"
            fontSize="15px"
            fontWeight={600}
          >
            Tháng {cursor.format("MM/YYYY")}
          </Text>
        </Flex>
        <Flex as="button" onClick={goDetail} align="center" gap="6px" color="white">
          <Text fontSize="14px">Chi tiết</Text>
          <Text fontSize="16px">›</Text>
        </Flex>
      </Flex>

      {/* ===== Lịch ===== */}
      <Box bg="white" px="10px" pt="8px" pb="10px" border="1px solid" borderColor={CARD_BORDER}>
        {calQuery.isLoading ? (
          <Loading label="Đang tải lịch..." />
        ) : (
          <>
            <Grid templateColumns="repeat(7, 1fr)" mb="4px">
              {WEEK_LABELS.map((w) => (
                <Text
                  key={w}
                  textAlign="center"
                  fontSize="13px"
                  fontWeight={700}
                  color={MUTED}
                  py="6px"
                >
                  {w}
                </Text>
              ))}
            </Grid>
            <Grid templateColumns="repeat(7, 1fr)" gap="4px">
              {days.map((d) => {
                const key = d.format("YYYY-MM-DD");
                const inMonth = d.month() === cursor.month();
                const type = statusByDate[key];
                const meta = type ? attendanceMeta(type) : null;
                const isToday = d.isSame(moment(), "day");
                const bg = meta ? meta.color : "white";
                const fg = meta ? "white" : TEXT;
                const lg = meta ? "rgba(255,255,255,0.85)" : "#9CA3AF";
                return (
                  <Flex
                    key={key}
                    as="button"
                    onClick={() => inMonth && openDay(key)}
                    direction="column"
                    align="center"
                    justify="center"
                    h="52px"
                    borderRadius="10px"
                    bg={bg}
                    border="1px solid"
                    borderColor={isToday ? PRIMARY : CARD_BORDER}
                    opacity={inMonth ? 1 : 0.35}
                    cursor={inMonth ? "pointer" : "default"}
                    _hover={inMonth ? { opacity: inMonth ? 0.9 : 0.35 } : {}}
                    transition="opacity .1s"
                  >
                    <Text fontSize="15px" fontWeight={700} color={fg} lineHeight="1.1">
                      {d.date()}
                    </Text>
                    <Text fontSize="11px" color={lg} mt="2px" lineHeight="1">
                      {lunarLabel(key)}
                    </Text>
                  </Flex>
                );
              })}
            </Grid>
          </>
        )}
      </Box>

      {/* ===== Chú thích (legend) 2 cột ===== */}
      <Box mt="10px" bg="white" borderRadius="14px" border="1px solid" borderColor={CARD_BORDER} p="12px">
        <Grid templateColumns="repeat(2, 1fr)" gap="12px">
          {LEGEND_ITEMS.map((it) => {
            const meta = attendanceMeta(it.key);
            return (
              <Flex key={it.key} align="center" gap="8px">
                <Box w="16px" h="16px" borderRadius="5px" bg={meta.color} border="1px solid" borderColor={CARD_BORDER} flexShrink={0} />
                <Text fontSize="13px" color={MUTED}>
                  {it.label}: {legendRate?.[it.key] ?? 0}
                </Text>
              </Flex>
            );
          })}
          <Flex align="center" gap="8px">
            <Box w="16px" h="16px" borderRadius="5px" bg="white" border="1px solid" borderColor={PRIMARY} flexShrink={0} />
            <Text fontSize="13px" color={MUTED}>
              Chưa chấm công: {legendCounts?.MISSING ?? 0}
            </Text>
          </Flex>
        </Grid>
      </Box>

      {/* ===== Banner chấm công ===== */}
      <Flex
        mt="10px"
        bg="white"
        borderTop="1px solid"
        borderColor={CARD_BORDER}
        p="12px"
        align="center"
        justify={checkedIn ? "flex-start" : "space-between"}
        gap="10px"
      >
        {checkedIn ? (
          <>
            <Box as={FiCheckCircle} color="#18C758" fontSize="20px" flexShrink={0} />
            <Text fontSize="14px" fontWeight={600} color={TEXT}>
              {bannerText}
            </Text>
          </>
        ) : (
          <>
            <Text fontSize="14px" fontWeight={600} color={TEXT} maxW="55%">
              {bannerText}
            </Text>
            <Flex
              as="button"
              onClick={() => canCheckIn && navigate(`${EMP_BASE_PATH}/cham-cong`)}
              align="center"
              gap="6px"
              bg={PRIMARY}
              color="white"
              h="48px"
              px="16px"
              borderRadius="8px"
              fontSize="15px"
              fontWeight={700}
              opacity={canCheckIn ? 1 : 0.6}
              cursor={canCheckIn ? "pointer" : "not-allowed"}
            >
              Chấm công
              <Box as={FaFingerprint} fontSize="20px" />
            </Flex>
          </>
        )}
      </Flex>

      {modals}
      </Box>
    </Box>
  );
};

export default AttendanceCalendar;
